
import React, { useState } from 'react';
import { Offcanvas, ListGroup, Button, InputGroup } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'sonner';
import { IconCheck, IconX } from '@tabler/icons-react';
import ErrorState from './ErrorState';
import useSyncUserRoles from '../../../services/user-v4/hooks/useSyncUserRoles';
import { useRoles } from '../../roles/hooks/useRoles';

interface AssignRoleDialogV4Props {
    show: boolean;
    onClose: () => void;
    user: { id: string; name: string; roles: string[] } | null;
    refetchUser?: () => void; // Optional, if you want to refetch user data after role assignment
}

const AssignRoleDialogV4: React.FC<AssignRoleDialogV4Props> = ({ show, onClose, user, refetchUser }) => {

    const [roleToAssign, setRoleToAssign] = useState<{ value: string; label: string } | null>(null);
    const { mutate: syncRoles, isPending } = useSyncUserRoles();

    // Fetch roles list using useRoles hook
    const { data: rolesData, isLoading: isRolesLoading, isError: isRolesError } = useRoles({
        name: 'roles',
        page: 1,
        limit: 100,
        search: '',
    });

    // Handler untuk menutup dialog
    const handleClose = () => {
        setRoleToAssign(null);
        onClose();
    };

    // Handler untuk assign role
    const handleAssign = () => {
        if (!roleToAssign || !user) return;
        const toastId = 'assign-role-v4';
        toast.loading('Assigning role...', { id: toastId });
        syncRoles({ user_id: user.id, roles: [...user.roles, roleToAssign.value] }, {
            onSuccess: () => {
                toast.success('Role assigned successfully.', { id: toastId });
                setRoleToAssign(null);
                if (refetchUser) {
                    refetchUser(); // Refetch user data if provided
                }
            },
            onError: (err: any) => {
                const errorMessage = err?.response?.data?.message || 'Failed to assign role.';
                toast.error(errorMessage, { id: toastId });
            }
        });
    };

    // Handler untuk remove role
    const handleRemove = (role: string) => {
        if (!user) return;
        const toastId = 'remove-role-v4';
        toast.loading('Removing role...', { id: toastId });
        const newRoles = user.roles.filter(r => r !== role);
        syncRoles({ user_id: user.id, roles: newRoles }, {
            onSuccess: () => {
                toast.success('Role removed successfully.', { id: toastId });
                if (refetchUser) {
                    refetchUser(); // Refetch user data if provided
                }
            },
            onError: (err: any) => {
                const errorMessage = err?.response?.data?.message || 'Failed to remove role.';
                toast.error(errorMessage, { id: toastId });
            }
        });
    };


    // Helper: transform rolesData to react-select options
    const roleOptions = rolesData?.data
        ? rolesData.data
            .filter((role: any) => !user?.roles.includes(role.name))
            .map((role: any) => ({ value: role.name, label: role.name }))
        : [];

    // Render logic untuk body Offcanvas
    const renderBody = () => {
        if (!user) return <ErrorState error={new Error('User not found.')} onRetry={handleClose} />;
        if (isRolesLoading) return <div className="p-4 text-center">Loading roles...</div>;
        if (isRolesError) return <ErrorState error={new Error('Failed to load roles.')} onRetry={handleClose} />;
        return (
            <fieldset disabled={isPending}>
                {/* Bagian untuk menambah role baru */}
                <div className="mb-4">
                    <label className="form-label fw-medium">Assign New Role</label>
                    <InputGroup>
                        <Select
                            className="flex-grow-1"
                            classNamePrefix="react-select"
                            options={roleOptions}
                            placeholder="Select a role to assign..."
                            value={roleToAssign}
                            onChange={setRoleToAssign}
                            isClearable
                        />
                        <Button variant="outline-success" onClick={handleAssign} disabled={!roleToAssign || isPending}>
                            <IconCheck size={18} />
                        </Button>
                    </InputGroup>
                </div>

                {/* Bagian untuk menampilkan daftar role yang sudah di-assign */}
                <div>
                    <label className="form-label fw-medium">Assigned Roles</label>
                    <div className="border rounded" style={{ minHeight: '60px', maxHeight: '40vh', overflowY: 'auto' }}>
                        {user.roles && user.roles.length > 0 ? (
                            <ListGroup variant="flush">
                                {user.roles.map(role => (
                                    <ListGroup.Item key={role} className="d-flex justify-content-between align-items-center">
                                        <span>{role}</span>
                                        <Button variant="light" size="sm" onClick={() => handleRemove(role)} disabled={isPending} title={`Remove ${role}`}>
                                            <IconX size={16} className="text-danger" />
                                        </Button>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        ) : (
                            <div className="d-flex align-items-center justify-content-center p-4 text-muted">
                                No roles assigned to this user.
                            </div>
                        )}
                    </div>
                </div>
            </fieldset>
        );
    };

    return (
        <Offcanvas show={show} onHide={handleClose} placement='end' backdrop="static" className="offcanvas-responsive">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Manage Roles for {user?.name}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                {renderBody()}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default AssignRoleDialogV4;
